import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string;
  streakStartDate: string;
  isStreakActive: boolean;
}

export const calculateStreak = (lastActivityDate: string): StreakData => {
  const today = new Date();
  const lastActivity = new Date(lastActivityDate);
  const todayStr = today.toISOString().split('T')[0];
  const lastActivityStr = lastActivity.toISOString().split('T')[0];
  
  // Calculate days difference
  const timeDiff = today.getTime() - lastActivity.getTime();
  const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
  
  let currentStreak = 0;
  let isStreakActive = false;
  
  if (daysDiff === 0) {
    // Activity today - streak continues
    currentStreak = 1; // This will be updated with actual streak from database
    isStreakActive = true;
  } else if (daysDiff === 1) {
    // Activity yesterday - streak continues
    currentStreak = 1; // This will be updated with actual streak from database
    isStreakActive = true;
  } else {
    // Streak broken
    currentStreak = 0;
    isStreakActive = false;
  }
  
  return {
    currentStreak,
    longestStreak: 0, // Will be updated from database
    lastActivityDate: lastActivityDate,
    streakStartDate: lastActivityDate,
    isStreakActive
  };
};

export const updateStreak = async (userId: string, activityType: 'journal' | 'module' | 'quiz'): Promise<StreakData> => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const streakDoc = await getDoc(doc(db, 'userStreaks', userId));
    
    let streakData: StreakData;
    
    if (streakDoc.exists()) {
      const data = streakDoc.data();
      const lastActivity = new Date(data.lastActivityDate);
      const todayDate = new Date();
      const daysDiff = Math.floor((todayDate.getTime() - lastActivity.getTime()) / (1000 * 3600 * 24));
      
      if (daysDiff === 0) {
        // Already active today - no change
        streakData = data;
      } else if (daysDiff === 1) {
        // Consecutive day - increment streak
        streakData = {
          currentStreak: data.currentStreak + 1,
          longestStreak: Math.max(data.currentStreak + 1, data.longestStreak),
          lastActivityDate: today,
          streakStartDate: data.streakStartDate,
          isStreakActive: true
        };
      } else {
        // Streak broken - reset
        streakData = {
          currentStreak: 1,
          longestStreak: data.longestStreak,
          lastActivityDate: today,
          streakStartDate: today,
          isStreakActive: true
        };
      }
    } else {
      // First activity
      streakData = {
        currentStreak: 1,
        longestStreak: 1,
        lastActivityDate: today,
        streakStartDate: today,
        isStreakActive: true
      };
    }
    
    // Update database
    await setDoc(doc(db, 'userStreaks', userId), streakData);
    
    return streakData;
  } catch (error) {
    console.error('Error updating streak:', error);
    throw error;
  }
};

export const getStreakData = async (userId: string): Promise<StreakData | null> => {
  try {
    const streakDoc = await getDoc(doc(db, 'userStreaks', userId));
    if (streakDoc.exists()) {
      return streakDoc.data() as StreakData;
    }
    return null;
  } catch (error) {
    console.error('Error getting streak data:', error);
    return null;
  }
};

export const checkStreakStatus = (streakData: StreakData): {
  isStreakActive: boolean;
  daysUntilBreak: number;
  streakWarning: boolean;
} => {
  const today = new Date();
  const lastActivity = new Date(streakData.lastActivityDate);
  const daysDiff = Math.floor((today.getTime() - lastActivity.getTime()) / (1000 * 3600 * 24));
  
  const isStreakActive = daysDiff <= 1;
  const daysUntilBreak = isStreakActive ? (daysDiff === 0 ? 1 : 0) : 0;
  const streakWarning = daysDiff === 1; // Warning if they haven't been active today
  
  return {
    isStreakActive,
    daysUntilBreak,
    streakWarning
  };
};
















